// src/context/AuthContext.jsx
"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase/config";
import { doc, onSnapshot } from "@/lib/firestore-instrumented";
import LoadingState from "@/components/LoadingState";

// Create the context
const AuthContext = createContext();

// Create a custom hook to easily use the context
export const useAuth = () => useContext(AuthContext);

// Create the provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileState, setProfileState] = useState({
    uid: null,
    data: null,
    loading: false,
    error: "",
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser || null);
      setProfileState({
        uid: nextUser?.uid || null,
        data: null,
        loading: Boolean(nextUser?.uid),
        error: "",
      });
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const uid = user.uid;
    let active = true;

    const unsubscribe = onSnapshot(
      doc(db, "users", uid),
      (snapshot) => {
        if (!active) return;
        setProfileState({
          uid,
          data: snapshot.exists() ? snapshot.data() || {} : null,
          loading: false,
          error: "",
        });
      },
      (error) => {
        if (!active) return;
        console.warn("[auth] failed to load user profile", error);
        setProfileState({
          uid,
          data: null,
          loading: false,
          error: "We couldn't load your profile.",
        });
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [user?.uid]);

  const profileMatchesUser = Boolean(user?.uid && profileState.uid === user.uid);
  const value = useMemo(() => ({
    user,
    profile: profileMatchesUser ? profileState.data : null,
    authLoading,
    profileLoading: profileMatchesUser ? profileState.loading : Boolean(user?.uid),
    profileError: profileMatchesUser ? profileState.error : "",
    loading: authLoading,
  }), [authLoading, profileMatchesUser, profileState, user]);

  return (
    <AuthContext.Provider value={value}>
      {authLoading ? (
      <LoadingState
        variant="page"
        message="Loading"
        showMessage={false}
      />
      ) : children}
    </AuthContext.Provider>
  );
};
